import { useCallback, useMemo } from 'react';
import { useCookieState } from 'ahooks';
import { useAppData } from '@modules/root';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';

const FILTER_DRAWER_COOKIE_NAME = 'showFilter';

/**
 * Hook to get and toggle the filter drawer open/close state. The state is
 * saved in the cookies.
 */
export function useFilterDrawerPreference() {
    // @ts-ignore
    const { filterDrawerCookieValue } = useAppData();
    const isDefaultFilterOpen = useABTestIsOn('default-filter-open');
    const [showFilterCookie, setShowFilterCookie] = useCookieState(
        FILTER_DRAWER_COOKIE_NAME,
        {
            // Use server cookie value if provided, otherwise fall back to A/B test default.
            defaultValue:
                filterDrawerCookieValue ?? String(isDefaultFilterOpen),
        }
    );

    const filterDrawerOpen = useMemo(
        () => showFilterCookie === 'true',
        [showFilterCookie]
    );

    const toggleFilterDrawer = useCallback(() => {
        setShowFilterCookie((state) => {
            const isDrawerOpen = state === 'true';
            return String(!isDrawerOpen);
        });
    }, [setShowFilterCookie]);

    return { filterDrawerOpen, toggleFilterDrawer };
}

/**
 * Get the filter drawer cookie value from the request headers.
 */
export function getFilterDrawerCookieValue(request: Request) {
    const cookieHeader = request.headers.get('cookie');
    return cookieHeader
        ?.split(';')
        .find((cookie) =>
            cookie.trim().startsWith(`${FILTER_DRAWER_COOKIE_NAME}=`)
        )
        ?.split('=')[1]
        ?.trim();
}
